import React, { useState } from "react";
import styled from "styled-components";
import Modal from "new_components/molecules/Modal";
import colors from "styles/colors";

// Constants for default theme settings
const DEFAULT_THEME = {
  buttonColor: "#2d70e2",
  highlightText: "#2d70e2",
  topBanner: "transparent",
  secondaryButton: "#EBF1FD",
  backgroundColor: "white",
  logoUrl: ""
};

// Fallback colors in case the imported colors don't have the expected structure
const fallbackColors = {
  text: {
    dark: "#2A3039",
    main: "#666D79",
    gray: "#666D79"
  },
  primary: {
    main: "#2D70E2",
    dark: "#2254AA"
  },
  background: {
    white: "#FFFFFF",
    gray: "#F2F2F2"
  },
  neutral: {
    500: "#DFDFDF"
  }
};

// Helper function to safely access colors or use fallbacks
const getColor = (path, fallback) => {
  try {
    const parts = path.split('.');
    let result = colors;
    for (const part of parts) {
      result = result[part];
      if (result === undefined) throw new Error('Path not found');
    }
    return result;
  } catch (e) {
    // If path doesn't exist in colors, use fallback
    const parts = path.split('.');
    let result = fallbackColors;
    for (const part of parts) {
      result = result[part] || fallback;
    }
    return result || fallback;
  }
};

const SettingsModal = ({ isOpen, onClose, themeSettings, onSaveSettings }) => {
  // Initialize settings state from props or defaults
  const [settings, setSettings] = useState(themeSettings || DEFAULT_THEME);
  
  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSettings({
      ...settings,
      [name]: value
    });
  };
  
  // Handle logo file upload
  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // In a real application, you would upload this file to a server
      // and get back a URL. For now, we'll use a local URL
      const logoUrl = URL.createObjectURL(file);
      setSettings({
        ...settings,
        logoUrl
      });
    }
  };
  
  // Handle clearing the logo
  const handleClearLogo = () => {
    setSettings({
      ...settings,
      logoUrl: ""
    });
  };
  
  // Handle save and close
  const handleSave = () => {
    onSaveSettings(settings);
    onClose();
  };
  
  // Handle transparent banner toggle
  const handleTransparentBanner = (e) => {
    setSettings({
      ...settings,
      topBanner: e.target.checked ? "transparent" : "#ffffff"
    });
  };
  
  // Handle resetting to default settings
  const handleResetDefaults = () => {
    setSettings({...DEFAULT_THEME});
  };
  
  // Modal content - form for settings
  const modalBody = (
    <SettingsContainer>
      <Section>
        <SectionTitle>Logo Settings</SectionTitle>
        <InputGroup>
          <Label>Upload Logo</Label>
          <FileInputWrapper>
            <FileInput 
              type="file" 
              accept="image/*" 
              onChange={handleLogoUpload} 
              id="logo-upload"
            />
            <FileInputLabel htmlFor="logo-upload">
              Choose File
            </FileInputLabel>
            <FileName>
              {settings.logoUrl ? "Logo uploaded" : "No file chosen"}
            </FileName>
            {settings.logoUrl && (
              <ClearButton onClick={handleClearLogo}>Clear</ClearButton>
            )}
          </FileInputWrapper>
          
          {settings.logoUrl && (
            <LogoPreview>
              <img src={settings.logoUrl} alt="Survey Logo Preview" />
            </LogoPreview>
          )}
        </InputGroup>
      </Section>
      
      <Section>
        <SectionTitle>Theme Colors</SectionTitle>
        
        <InputGroup>
          <Label>Primary Button Color</Label>
          <ColorInputWrapper>
            <ColorInput 
              type="color" 
              name="buttonColor" 
              value={settings.buttonColor} 
              onChange={handleInputChange} 
            />
            <ColorValue 
              type="text" 
              name="buttonColor" 
              value={settings.buttonColor} 
              onChange={handleInputChange} 
            />
          </ColorInputWrapper>
        </InputGroup>
        
        <InputGroup>
          <Label>Highlight Text Color</Label>
          <ColorInputWrapper>
            <ColorInput 
              type="color" 
              name="highlightText" 
              value={settings.highlightText} 
              onChange={handleInputChange} 
            />
            <ColorValue 
              type="text" 
              name="highlightText" 
              value={settings.highlightText} 
              onChange={handleInputChange} 
            />
          </ColorInputWrapper>
        </InputGroup>
        
        <InputGroup>
          <Label>Top Banner Color</Label>
          <ColorInputWrapper>
            <ColorInput 
              type="color" 
              name="topBanner" 
              value={settings.topBanner === "transparent" ? "#ffffff" : settings.topBanner} 
              onChange={handleInputChange} 
              disabled={settings.topBanner === "transparent"}
            />
            <ColorValue 
              type="text" 
              name="topBanner" 
              value={settings.topBanner} 
              onChange={handleInputChange} 
              disabled={settings.topBanner === "transparent"}
            />
            <CheckboxWrapper>
              <Checkbox 
                type="checkbox" 
                id="transparentBanner" 
                checked={settings.topBanner === "transparent"} 
                onChange={handleTransparentBanner}
              />
              <CheckboxLabel htmlFor="transparentBanner">Transparent</CheckboxLabel>
            </CheckboxWrapper>
          </ColorInputWrapper>
        </InputGroup>
        
        <InputGroup>
          <Label>Secondary Button Color</Label>
          <ColorInputWrapper>
            <ColorInput 
              type="color" 
              name="secondaryButton" 
              value={settings.secondaryButton} 
              onChange={handleInputChange} 
            />
            <ColorValue 
              type="text" 
              name="secondaryButton" 
              value={settings.secondaryButton} 
              onChange={handleInputChange} 
            />
          </ColorInputWrapper>
        </InputGroup>
        
        <InputGroup>
          <Label>Background Color</Label>
          <ColorInputWrapper>
            <ColorInput 
              type="color" 
              name="backgroundColor" 
              value={settings.backgroundColor} 
              onChange={handleInputChange} 
            />
            <ColorValue 
              type="text" 
              name="backgroundColor" 
              value={settings.backgroundColor} 
              onChange={handleInputChange} 
            />
          </ColorInputWrapper>
        </InputGroup>
      </Section>
    </SettingsContainer>
  );
  
  // Footer with action buttons
  const modalFooter = (
    <ModalFooter>
      <ResetButton onClick={handleResetDefaults}>
        Reset to Defaults
      </ResetButton>
      <ButtonGroup>
        <CancelButton onClick={onClose}>Cancel</CancelButton>
        <SaveButton onClick={handleSave}>Save Settings</SaveButton>
      </ButtonGroup>
    </ModalFooter>
  );
  
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      modalHeading="Survey Settings"
      modalBody={modalBody}
      modalFooter={modalFooter}
      modalWidth="600px"
      modalHeight="auto"
    />
  );
};

// Styled components
const SettingsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding: 8px 12px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  background-color: ${() => getColor('background.white', '#FFFFFF')};
  border-radius: 8px;
  padding: 4px 0;
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${() => getColor('text.dark', '#2A3039')};
  margin: 0;
  padding-bottom: 12px;
  border-bottom: 1px solid ${() => getColor('neutral.500', '#DFDFDF')};
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 16px;
`;

const Label = styled.label`
  font-size: 15px;
  font-weight: 500;
  color: ${() => getColor('text.dark', '#2A3039')};
`;

const ColorInputWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
`;

const ColorInput = styled.input`
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  opacity: ${props => props.disabled ? 0.5 : 1};
  
  &::-webkit-color-swatch-wrapper {
    padding: 0;
  }
  
  &::-webkit-color-swatch {
    border: 1px solid ${() => getColor('neutral.500', '#DFDFDF')};
    border-radius: 4px;
  }
`;

const ColorValue = styled.input`
  padding: 8px 12px;
  border: 1px solid ${() => getColor('neutral.500', '#DFDFDF')};
  border-radius: 4px;
  font-size: 14px;
  width: 100px;
  opacity: ${props => props.disabled ? 0.5 : 1};
`;

const FileInputWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
`;

const FileInput = styled.input`
  display: none;
`;

const FileInputLabel = styled.label`
  padding: 8px 16px;
  background-color: ${() => getColor('primary.500', '#2D70E2')};
  color: white;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: ${() => getColor('primary.600', '#2254AA')};
  }
`;

const FileName = styled.span`
  font-size: 14px;
  color: ${() => getColor('text.gray', '#666D79')};
`;

const ClearButton = styled.button`
  padding: 6px 12px;
  background-color: transparent;
  color: ${() => getColor('text.gray', '#666D79')};
  border: 1px solid ${() => getColor('neutral.500', '#DFDFDF')};
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: ${() => getColor('background.gray', '#F2F2F2')};
    color: ${() => getColor('text.dark', '#2A3039')};
  }
`;

const LogoPreview = styled.div`
  margin-top: 12px;
  max-width: 200px;
  background-color: #f9f9f9;
  padding: 12px;
  border-radius: 4px;
  
  img {
    width: 100%;
    height: auto;
    border-radius: 4px;
    border: 1px solid ${() => getColor('neutral.500', '#DFDFDF')};
  }
`;

const CheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-left: 4px;
`;

const Checkbox = styled.input`
  margin-right: 8px;
  cursor: pointer;
  width: 16px;
  height: 16px;
`;

const CheckboxLabel = styled.label`
  font-size: 14px;
  color: ${() => getColor('text.gray', '#666D79')};
  cursor: pointer;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 16px;
  border-top: 1px solid ${() => getColor('neutral.500', '#DFDFDF')};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
`;

const Button = styled.button`
  padding: 10px 20px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
`;

const ResetButton = styled(Button)`
  background-color: transparent;
  border: none;
  color: ${() => getColor('text.gray', '#666D79')};
  padding-left: 0;
  
  &:hover {
    color: ${() => getColor('primary.500', '#2D70E2')};
    text-decoration: underline;
  }
`;

const CancelButton = styled(Button)`
  background-color: white;
  border: 1px solid ${() => getColor('neutral.500', '#DFDFDF')};
  color: ${() => getColor('text.gray', '#666D79')};
  
  &:hover {
    background-color: ${() => getColor('background.gray', '#F2F2F2')};
  }
`;

const SaveButton = styled(Button)`
  background-color: ${() => getColor('primary.500', '#2D70E2')};
  border: 1px solid ${() => getColor('primary.500', '#2D70E2')};
  color: white;
  
  &:hover {
    background-color: ${() => getColor('primary.600', '#2254AA')};
  }
`;

export default SettingsModal; 